import React, {useState, useRef} from "react";
import styled from "styled-components";
import {useInventory, InventoryQuantity} from "../State/InventoryContext";
import {InventoryPropertiesEnum, ItemQuantity} from "../State/Inventory";
import {CheatPlacementOptions, EmptySlot, EmptyMinerPopup} from "../SharedComponents/EmptySlot";
import {ManualMiner} from "./ManualMiner";
import {MachineCard} from "../SharedComponents/MachineCard";
import {MINER_BEHAVIORS} from "../State/GameData";
import {FieldHeader} from "../SharedComponents/FieldHeader";
import {useNavigate} from "react-router-dom";
import {PatchUnlockModal} from "./PatchUnlockModal";
import {useCheatKey} from "../Utility/useCheatKey";
import {BsFuelPump} from "react-icons/bs";
import {useOutsideAlerter} from "../Utility/useOutsideAlerter";

const M = {
    FuelControlContainer: styled.div`
      width: 540px;
      margin-bottom: 16px;
      padding: 12px;
      background: #f5f5f5;
      border-radius: 4px;
      border: 2px solid #ddd;
      display: flex;
      align-items: center;
      justify-content: space-between;
    `,
    FuelControlLabel: styled.div`
      font-size: 14px;
      font-weight: bold;
      color: #333;
    `,
    FuelControlButtons: styled.div`
      display: flex;
      align-items: center;
      gap: 8px;
    `,
    FuelControlButton: styled.button`
      width: 32px;
      height: 32px;
      border-radius: 4px;
      border: 2px solid #666;
      background: #fff;
      color: #333;
      font-size: 18px;
      font-weight: bold;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover {
        background: #e0e0e0;
      }
      &:active {
        transform: scale(0.95);
      }
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
    FuelControlValue: styled.div`
      font-size: 16px;
      font-weight: bold;
      color: #ff9800;
      min-width: 40px;
      text-align: center;
    `,
    FieldContainer: styled.div`
      width: 651px;
      margin-bottom: 32px;
    `,
    MiningField: styled.div<{ $type: string }>`
      border-radius: 4px;
      width: 651px;
      height: 253px;
      padding-top: 16px;
      padding-bottom: 16px;
      padding-left: 16px;
      display: flex;
      justify-content: space-between;

      ${(props) => ((props.$type === "coal") &&
          `background-color: #5d2b00;
            border: 2px solid black;`)
              || ((props.$type === "iron") && `
            background-color: #8D918D;
            border: 2px solid black;
            `)
              || ((props.$type === "copper") && `
            background-color: #E0791E;
            border: 2px solid black;
            `)
              || ((props.$type === "stone") && `
            background-color: #b3a089;
            border: 2px solid black;
            `)
      }
    `,
    MiningFieldInner: styled.div`
      width: 530px;
      display: flex;
      flex-wrap: wrap;
    `,
    PatchStatus: styled.small`
      display: block;
      margin-left: 8px;
      margin-bottom: 8px;
    `,
    LockedSlot: styled.div`
      background-color: #d9d9d9;
      border-radius: 8px;
      border: 4px dashed #b0b0b0;
      color: #6c757d;
      width: 90px;
      height: 100px;
      margin-right: 16px;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      text-align: center;
      cursor: pointer;
      transition: background-color 0.2s ease, color 0.2s ease;
      &:hover {
        background-color: #c4c4c4;
        color: #464646;
      }
    `,
    FuelSelect: styled.div`
      width: 80px;
      display: flex;
      flex-direction: column;
      padding-bottom: 16px;
      align-items: center;
      gap: 8px;
    `,
    ManualButton: styled.div`
      background-color: lightgrey;
      border-radius: 8px;
      border: 4px solid white;
      width: 70px;
      height: 70px;
      cursor: pointer;
      box-shadow: 2px 2px;
    `,
    SidePanel: styled.div`
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
}

function FuelSelection(props: { type: string, options: InventoryQuantity[], selectedFuel: string | null }) {
    const {addFuelToMiner} = useInventory()
    const [popupIsOpen, setPopupIsOpen] = useState(false);
    const wrapperRef = useRef(null)
    const closePopup = () => {
        setPopupIsOpen(false);
    }
    useOutsideAlerter(wrapperRef, closePopup)

    const pickedFuel = (itemId: string) => {
        addFuelToMiner(props.type, itemId)
    }

    return <M.FuelSelect>
        <M.ManualButton ref={wrapperRef}>
            <EmptyMinerPopup isOpen={popupIsOpen} callback={pickedFuel} options={props.options}/>
            <BsFuelPump size={62}
                        onClick={() => setPopupIsOpen(true)}
            />
            {props.selectedFuel ? props.selectedFuel : "None"}
        </M.ManualButton>
    </M.FuelSelect>
}

function Field(props: { type: string, onNoFuelClick?: () => void, blinkManualMiner?: boolean }) {
    const {inventory, toggleMiner, addMiner, removeMiner, updateInventoryItem, getItemQuantity, completePatchUnlock, hasTechnology, addFuelToMinerBuffer, setFuelAmountPerClick, getItemsByType} = useInventory()
    const navigate = useNavigate();
    const [showUnlockModal, setShowUnlockModal] = useState(false)
    const cheatActive = useCheatKey("c")
    const isTreeField = ["coal", "stone"].includes(props.type)
    const clearingUnlocked = isTreeField ? hasTechnology("tech-wood-clearing") : hasTechnology("tech-boulder-clearing")
    const treeHitBonus = hasTechnology("tech-steel-axes") ? 1 : 0
    const treeHold = hasTechnology("tech-chainsaw")
    const boulderHold = hasTechnology("tech-big-drills")
    const hasAutoFueling = hasTechnology("tech-auto-miner-fueling")
    const fuelOptions = getItemsByType(InventoryPropertiesEnum.fuel)

    const minerState = inventory.minerState
    const fieldState = minerState.find(it => it.resourceType === props.type)
    if (!fieldState) {
        return null
    }
    const nrOfCurrentMiners = fieldState.miners.length

    const clearedSpots = fieldState.clearedSpots ?? 0
    const maxSlots = Math.min(10, clearedSpots)
    const lockedSlots = Math.max(0, 10 - clearedSpots)
    const canAddMoreMiners = nrOfCurrentMiners < maxSlots
    const fillerSlots = Math.max(maxSlots - nrOfCurrentMiners - (canAddMoreMiners ? 1 : 0), 0)

    const miners = inventory.inventory
        .filter(invQ => invQ?.item.properties.includes(InventoryPropertiesEnum.miner))

    const handleUnlockComplete = (rewards: ItemQuantity[]) => {
        completePatchUnlock(props.type, rewards)
        setShowUnlockModal(false)
    }

    const handleUnlockClick = () => {
        if (!clearingUnlocked) {
            return
        }
        if (cheatActive) {
            completePatchUnlock(props.type, [])
            setShowUnlockModal(false)
            return
        }
        setShowUnlockModal(true)
    }

    const addedMiner = (itemId: string, options?: CheatPlacementOptions) => {
        addMiner(props.type, itemId)
        if (!options?.isCheat) {
            updateInventoryItem(itemId, -1);
        }
    }

    const minersRunning = fieldState.miners.filter(miner => miner.running).length
    const productionPerSecond = fieldState.miners.reduce((sum, miner) => {
        const behavior = MINER_BEHAVIORS[miner.itemId]
        if (!miner.running || !behavior || !behavior.supportedResources.includes(props.type)) {
            return sum
        }
        return sum + (behavior.outputPerCycle / behavior.cycleTimeMs) * 1000
    }, 0)
    const coalPerSecond = fieldState.miners.reduce((sum, miner) => {
        const behavior = MINER_BEHAVIORS[miner.itemId]
        if (!miner.running || !behavior?.fuelCostPerCycle) {
            return sum
        }
        return sum + (behavior.fuelCostPerCycle.quantity / behavior.cycleTimeMs) * 1000
    }, 0)

    const requestCraft = (itemId: string) => {
        navigate("/create", {state: {targetItemId: itemId}});
    }

    return <M.FieldContainer>
        <FieldHeader
            title={`${props.type.charAt(0).toUpperCase() + props.type.slice(1)} Field`}
            running={minersRunning}
            total={10}
            productionPerSecond={productionPerSecond}
            fuelPerSecond={coalPerSecond}
        />
        <M.PatchStatus>{clearedSpots} / 10 patches cleared. {lockedSlots > 0 && (clearingUnlocked ? "Click a locked patch to clear the next spot." : isTreeField ? "Research Wood Clearing to clear trees." : "Research Boulder Clearing to smash rocks.")}</M.PatchStatus>
        <M.MiningField $type={props.type} data-testid={`${props.type}-field`}>
            <M.MiningFieldInner>
                {fieldState.miners.map((item, index) => {
                    const behavior = MINER_BEHAVIORS[item.itemId]
                    const fuelQuantity = behavior?.fuelCostPerCycle ? getItemQuantity(behavior.fuelCostPerCycle.itemId) : 0
                    const hasAutoFueling = hasTechnology("tech-auto-miner-fueling")
                    return (
                        <Miner
                            key={index}
                            minerId={item.itemId}
                            isSpinning={item.running}
                            progressMs={item.progressMs}
                            callback={() => toggleMiner(props.type, index)}
                            fuelAvailable={fuelQuantity}
                            fuelBuffer={item.fuelBuffer}
                            autoFuelingEnabled={hasAutoFueling}
                            onAddFuel={() => addFuelToMinerBuffer(props.type, index)}
                            onRemove={() => removeMiner(props.type, index)}
                            onNoFuelClick={props.onNoFuelClick}
                        />
                    )
                })}
                {canAddMoreMiners && <EmptySlot type={props.type} clickable={true} options={miners} addedItem={addedMiner}
                                                onRequestCraft={requestCraft}/>}

                {Array.from({length: fillerSlots}).map((_, index) => (
                    <EmptySlot
                        key={`filler-${index}`}
                        type={props.type}
                        clickable={false}
                    />
                ))}

                {Array.from({length: lockedSlots}).map((_, index) => <M.LockedSlot key={`locked-${index}`}
                                                                                   onClick={handleUnlockClick}>
                    Clear patch
                    </M.LockedSlot>)}

            </M.MiningFieldInner>
            <M.SidePanel>
                <ManualMiner type={props.type} blink={props.blinkManualMiner} />
                {hasAutoFueling && <FuelSelection type={props.type} options={fuelOptions} selectedFuel={fieldState.fuelType ?? null} />}
            </M.SidePanel>
        </M.MiningField>
        {lockedSlots > 0 && <PatchUnlockModal resourceType={props.type}
                                              clearedSpots={clearedSpots}
                                              isOpen={showUnlockModal}
                                              onClose={() => setShowUnlockModal(false)}
                                              treeHitBonus={treeHitBonus}
                                              enableTreeHold={treeHold}
                                              enableBoulderHold={boulderHold}
                                              onComplete={handleUnlockComplete}/>}
    </M.FieldContainer>
}

type MinerProps = {
    minerId: string,
    isSpinning: boolean,
    progressMs: number,
    callback: () => void,
    fuelAvailable: number,
    fuelBuffer?: number,
    autoFuelingEnabled?: boolean,
    onAddFuel?: () => void,
    onRemove?: () => void,
    onNoFuelClick?: () => void,
}

function Miner({minerId, isSpinning, progressMs, callback, fuelAvailable, fuelBuffer, autoFuelingEnabled, onAddFuel, onRemove, onNoFuelClick}: MinerProps) {
    const {inventory} = useInventory();
    const behavior = MINER_BEHAVIORS[minerId]
    const cycleTimeMs = behavior?.cycleTimeMs ?? 1000
    const needsFuel = behavior?.fuelCostPerCycle
    const showFuelWarning = !!needsFuel && fuelAvailable < (needsFuel?.quantity ?? 0)
    const fuelAmountPerClick = inventory.fuelAmountPerClick ?? 1;

    // Look up the actual item to get its name and power use
    const minerItem = inventory.inventory.find(item => item.item.id === minerId);
    const minerName = minerItem?.item.name ?? "Miner";
    const powerUse = minerItem?.item.powerUse;

    // Electric miners use power, burner miners use fuel
    const isElectric = !needsFuel && powerUse !== undefined;

    // Calculate minimum fuel for one cycle (0.1 MJ per ore * outputPerCycle / 1 MJ per coal)
    const minFuelForOneCycle = needsFuel && behavior ? (0.1 * behavior.outputPerCycle) : undefined;

    // Calculate production rate (items per second)
    const productionRate = behavior ? (behavior.outputPerCycle / behavior.cycleTimeMs) * 1000 : undefined;

    // For electric miners, check if we have enough power
    // For now, show power warning if running and not enough power
    // TODO: Implement actual power checking against power grid
    const showPowerWarning = false; // Will implement power checking later

    return <MachineCard
        label={minerName}
        running={isSpinning}
        progressMs={progressMs}
        cycleTimeMs={cycleTimeMs}
        onToggle={callback}
        showFuelWarning={isElectric ? showPowerWarning : showFuelWarning}
        fuelBuffer={needsFuel ? fuelBuffer : undefined}
        autoFuelingEnabled={autoFuelingEnabled}
        onAddFuel={needsFuel ? onAddFuel : undefined}
        fuelAmountPerClick={fuelAmountPerClick}
        onRemove={onRemove}
        minFuelForOneCycle={minFuelForOneCycle}
        machineItemId={minerId}
        productionRate={productionRate}
        powerUse={powerUse}
        dataTestId="miner-card"
        fuelAvailable={fuelAvailable}
        onNoFuelClick={onNoFuelClick}
    />
}


function FuelControl() {
    const {inventory, setFuelAmountPerClick} = useInventory();
    const currentAmount = inventory.fuelAmountPerClick ?? 1;

    const decrease = () => {
        if (currentAmount > 1) {
            setFuelAmountPerClick(currentAmount - 1);
        }
    };

    const increase = () => {
        if (currentAmount < 10) {
            setFuelAmountPerClick(currentAmount + 1);
        }
    };

    return (
        <M.FuelControlContainer>
            <M.FuelControlLabel>Fuel per Click:</M.FuelControlLabel>
            <M.FuelControlButtons>
                <M.FuelControlButton onClick={decrease} disabled={currentAmount <= 1}>
                    −
                </M.FuelControlButton>
                <M.FuelControlValue>{currentAmount}</M.FuelControlValue>
                <M.FuelControlButton onClick={increase} disabled={currentAmount >= 10}>
                    +
                </M.FuelControlButton>
            </M.FuelControlButtons>
        </M.FuelControlContainer>
    );
}

export function Mining() {
    const [blinkCoalMiner, setBlinkCoalMiner] = useState(false)

    const handleNoFuelClick = () => {
        setBlinkCoalMiner(true)
        setTimeout(() => setBlinkCoalMiner(false), 1200) // 3 blinks at 400ms each
    }

    return <>
        <FuelControl />
        <Field type={"coal"} blinkManualMiner={blinkCoalMiner} onNoFuelClick={handleNoFuelClick}/>
        <Field type={"iron"} onNoFuelClick={handleNoFuelClick}/>
        <Field type={"copper"} onNoFuelClick={handleNoFuelClick}/>
        <Field type={"stone"} onNoFuelClick={handleNoFuelClick}/>
    </>
}